import { yupResolver } from "@hookform/resolvers/yup";
import { Checkbox, Col, Form, Row, Space } from "antd";
import dayjs from "dayjs";
import { cloneDeep, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import entityType from "../../api/entityType";
import { transformCoverageSummaryInput } from "../../store/coverage/coverageActions";
import {
  exportReport,
  fetchReport,
  resetReportState,
} from "../../store/report/reportActions";
import PermissionWrapper from "../navigation/PermissionWrapper";
import ReportPanel from "../shared/ReportPanel";
import AgencyMultiSelectEx from "../shared/form/AgencyMultiSelectEx";
import DrillDownSortFieldSelectEx from "../shared/form/DrillDownSortFieldSelectEx";
import ExtraReportFilterContainer from "../shared/form/ExtraReportFiltersContainer";
import ProducerSelectEx from "../shared/form/ProducerSelectEx";
import {
  dateRangeConfigs,
  fixedDateRanges,
} from "../shared/form/fixedDateRanges";
import { getInitialAgency } from "../shared/form/getInitialAgency";
import AdvancedRangePicker from "../shared/form/reactHookForm/AdvancedRangePicker";
import { dateFormat, toEndOfDay, toStartOfDay } from "../shared/formatting";
import { ViewProductivityDataFullAccess } from "../shared/securityRoleTasks";
import { agenciesListEqual } from "../shared/sorting";
import { coverageSummaryReportSchema } from "./coverageSchemas";
import { drillDownSortFieldOptions } from "./reportParameters";
import useUserPreferences from "../../hooks/useUserPreferences";

// Constants
const partialInitialValues = {
  AgencyKeys: [],
  FixedDateRange: fixedDateRanges.YTD,
  TransactionDate: ["", ""],
  IncludeNonFleetNonGarage: true,
  IncludeFleetGarage: true,
  DrillDownSortFields: [],
  ProducerCode1Keys: [],
  ProducerCode2Keys: [],
  ProducersTaskKey: ViewProductivityDataFullAccess,
};

const defaultLayout = {
  labelCol: {
    xs: { span: 5 },
    sm: { span: 5 },
    md: { span: 5 },
    lg: { span: 8 },
    xl: { span: 6 },
    xxl: { span: 5 },
  },
  wrapperCol: {
    xs: { span: 19 },
    sm: { span: 19 },
    md: { span: 19 },
    lg: { span: 16 },
    xl: { span: 18 },
    xxl: { span: 19 },
  },
};

const CoverageSummaryReport = () => {
  // Form
  const {
    handleSubmit,
    reset,
    control,
    getValues,
    setValue,
    trigger,
    formState: { isValid, isDirty, defaultValues, isSubmitted },
  } = useForm({
    mode: "onChange",
    defaultValues: partialInitialValues,
    resolver: yupResolver(coverageSummaryReportSchema),
  });

  //
  const dispatch = useDispatch();

  //
  const [currentAgencies, setCurrentAgencies] = useState([]);
  const [initialDefaultValues, setInitialDefaultValues] =
    useState(partialInitialValues);

  //
  const homeAgency = useSelector((state) => state.actor.details.data.AgencyKey);
  const agencies = useSelector((state) => state.agency.listCompact);
  const { preferences } = useUserPreferences(["UseYesterdayInToDateShortcuts"]);
  const toDateEndingIsYesterday = preferences?.UseYesterdayInToDateShortcuts;

  //
  useEffect(() => {
    if (
      agencies != null &&
      !isEmpty(agencies) &&
      !agenciesListEqual(currentAgencies, agencies)
    ) {
      setCurrentAgencies(agencies);
      var initialAgency = getInitialAgency(homeAgency, agencies);
      const fullInitialValues = {
        ...partialInitialValues,
        AgencyKeys: [initialAgency],
        TransactionDate: [
          toStartOfDay(dayjs().startOf("year")),
          toEndOfDay(dayjs().endOf("day")),
        ],
      };
      setInitialDefaultValues(cloneDeep(fullInitialValues));
      reset(fullInitialValues);
      trigger();
    }
  }, [agencies, homeAgency]);

  const reportName = "CoverageSummaryReport";

  const onSubmitHandler = (values) => {
    dispatch(
      fetchReport(
        transformCoverageSummaryInput(values),
        entityType.COVERAGE,
        `PrintSummary`,
        () => {
          reset(values);
        }
      )
    );
  };

  const handlers = {
    onSubmit: () => handleSubmit(onSubmitHandler)(),
    onExport: () =>
      dispatch(
        exportReport(
          transformCoverageSummaryInput(getValues()),
          reportName,
          entityType.COVERAGE
        )
      ),
    onRestore: () => {
      reset(initialDefaultValues);
      dispatch(resetReportState());
    },
  };

  const buttons = [
    {
      action: "preview",
      disabled: !isValid,
    },
    {
      action: "export",
      disabled: !isValid,
      onClick: handlers.onExport,
    },
    { action: "restore", onClick: handlers.onRestore },
  ];

  return (
    <PermissionWrapper entityName="CoverageSummaryReport">
      <ReportPanel
        isDirty={isDirty}
        isSubmitted={isSubmitted}
        onSubmit={handlers.onSubmit}
        buttons={buttons}
        taskId={ViewProductivityDataFullAccess}
      >
        <Row>
          <Col xs={24} sm={24} md={24} lg={10} xl={10} xxl={10}>
            <AgencyMultiSelectEx
              layout={defaultLayout}
              formLabel="Agency"
              required={true}
              agencies={currentAgencies}
              isLoading={isEmpty(defaultValues.AgencyKeys)}
              setValue={setValue}
              control={control}
              name="AgencyKeys"
            />
          </Col>
          <Col xs={24} sm={24} md={24} lg={14} xl={14} xxl={14}>
            <AdvancedRangePicker
              formItem={{
                labelCol: {
                  xs: { span: 5 },
                  sm: { span: 7 },
                  md: { span: 6 },
                  lg: { span: 8 },
                  xl: { span: 6 },
                  xxl: { span: 5 },
                },
                wrapperCol: {
                  xs: { span: 19 },
                  sm: { span: 17 },
                  md: { span: 18 },
                  lg: { span: 16 },
                  xl: { span: 18 },
                  xxl: { span: 19 },
                },
                required: true,
                label: "Transaction Date",
              }}
              control={control}
              setValue={setValue}
              allowClear={true}
              name="TransactionDate"
              format={dateFormat}
              dateRangeConfig={dateRangeConfigs.TRANSACTION}
              toDateEndingIsYesterday={toDateEndingIsYesterday}
            />

            <Form.Item
              {...defaultLayout}
              label="Include"
              name="Include"
              required
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Space direction="vertical">
                <Controller
                  control={control}
                  name="IncludeNonFleetNonGarage"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => {
                        setValue("IncludeNonFleetNonGarage", e.target.checked, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        if (
                          !getValues().IncludeFleetGarage &&
                          !e.target.checked
                        ) {
                          setValue("IncludeFleetGarage", true, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }}
                      inputRef={field.ref}
                      name="IncludeNonFleetNonGarage"
                    >
                      Non-Fleet and Non-Garage Policies
                    </Checkbox>
                  )}
                />
                <Controller
                  control={control}
                  name="IncludeFleetGarage"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      name="IncludeFleetGarage"
                      onChange={(e) => {
                        setValue("IncludeFleetGarage", e.target.checked, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        if (
                          !getValues().IncludeNonFleetNonGarage &&
                          !e.target.checked
                        ) {
                          setValue("IncludeNonFleetNonGarage", true, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                        disableFleetGarageOption(e.target.checked);
                      }}
                      inputRef={field.ref}
                    >
                      Fleet and Garage Policies
                    </Checkbox>
                  )}
                />
              </Space>
            </Form.Item>
          </Col>
        </Row>
        <ExtraReportFilterContainer>
          <Row>
            <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
              <Form.Item
                name="ProducerCode1Keys"
                label="Producer 1"
                {...defaultLayout}
              >
                <ProducerSelectEx
                  name="ProducerCode1Keys"
                  setValue={setValue}
                  control={control}
                  producerNumber={1}
                  taskKey={ViewProductivityDataFullAccess}
                />
              </Form.Item>
              <Form.Item
                name="ProducerCode2Keys"
                label="Producer 2"
                {...defaultLayout}
              >
                <ProducerSelectEx
                  name="ProducerCode2Keys"
                  setValue={setValue}
                  control={control}
                  producerNumber={2}
                  taskKey={ViewProductivityDataFullAccess}
                />
              </Form.Item>
            </Col>
          </Row>
        </ExtraReportFilterContainer>

        <Row>
          <Col span={24}>
            <DrillDownSortFieldSelectEx
              fieldOptions={drillDownSortFieldOptions}
              control={control}
              trigger={trigger}
              isSubmitted={isSubmitted}
            />
          </Col>
        </Row>
      </ReportPanel>
    </PermissionWrapper>
  );
};
export default CoverageSummaryReport;
